<!-- eslint-disable max-len -->
<script setup lang='ts'>
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'

const {
    Question,
    Button: PocketButton,
    Icon,
} = UIKit

const emit = defineEmits<{
    'seeStats': [hasTakenExam: boolean]
}>()

const questionContainerEl = ref<HTMLElement | null>(null)
const checkedAnswer = ref(false)
const isCorrectAnswer = ref(false)

const question = computed(() => {
    return {
        'objectId': 'XDPLJZdDh1',
        'createdAt': '2025-10-30T19:26:44.758Z',
        'updatedAt': '2025-10-30T19:26:44.758Z',
        'subject': {
            'objectId': '5464358.349832681',
            'className': 'Subject',
            '__type': 'Pointer' as const,
        },
        'choices': [
            {
                'text': '<p>Alert</p>',
                'isCorrect': true,
                'id': 'a1',
            },
            {
                'text': '<p>Verbal</p>',
                'isCorrect': false,
                'id': 'd1',
            },
            {
                'text': '<p>Pain</p>',
                'isCorrect': false,
                'id': 'd2',
            },
            {
                'text': '<p>Unresponsive</p>',
                'isCorrect': false,
                'id': 'd3',
            },
        ],
        'references': [
            '<p><i>Emergency Care and Transportation of the Sick and Injured Essentials Package, 12th Edition (ISBN 978-1284246223)</i>. Pg 607-608.</p>',
        ],
        'addedDate': {
            '__type': 'Date' as const,
            'iso': '2025-10-01T16:30:52.698Z',
        },
        'prompt': '<p>A bystander points to an elderly woman slumped over on a bus stop bench. As you approach, she looks up at you and says, \"Hello. I think I fell asleep.\"&nbsp;</p><p>What is her level of responsiveness?</p>',
        'explanation': '<p><strong>Correct answer: </strong>Alert</p><p>This patient is classified as Alert. She is spontaneously aware of her surroundings, recognizes your approach, initiates conversation, and provides a coherent response. Her ability to do all of this without a direct prompt from you indicates that her brain is functioning at its highest level of consciousness. The AVPU scale\'s "A" stands for a fully conscious state.</p><p>Verbal: She did not require a verbal prompt to respond; her response was spontaneous. Therefore, she is at a higher level of consciousness than a "Verbal" patient.</p><p>Painful: There is no need to apply a painful stimulus, as she is fully responsive.</p><p>Unresponsive: The patient is clearly conscious and able to communicate.</p>',
        'serial': 'CsKrga98Ww',
        'examMetadata': {
            '__type': 'Pointer' as const,
            'className': 'ExamMetadata',
            'objectId': 'ZnndmJzAAC',
        },
        'compositeKey': 'ppef01/2.3.4',
        'type': 'Multiple Choice' as const,
        'isArchived': false,
        'isFree': false,
        'isMockQuestion': false,
    }
})

onMounted(() => {
    questionContainerEl.value = document.getElementById('try-a-question') || null
})

const checkAnswer = (answer: Study.Cloud.IQuizAnswer) => {
    // After the question is checked we do not need the UI-Kit Question component's Next Button
    // We will have a button that will take the user to the next screen
    const questionActionContainer = document.querySelector('.uikit-question__action-container')
    if (questionActionContainer) {
        (questionActionContainer as HTMLElement).style.display = 'none'
        checkedAnswer.value = true
    }
    isCorrectAnswer.value = answer.isCorrect
}

const goToStats = () => {
    emit('seeStats', isCorrectAnswer.value)
}
</script>

<template>
    <div class="try-a-question">
        <div class="try-a-question__banner">
            <img
                class="try-a-question__half-tone"
                src="@/assets/register-onboard/try-a-question/banner-half-tone.png"
                alt=""
            />
            <Icon class="try-a-question__star" type="star" />
            <div class="try-a-question__banner-text">
                {{ checkedAnswer ?
                    'Each of our questions has an answer explanation for deeper learning' :
                    'All of our questions are written by subject matter experts.'
                }}
            </div>
        </div>
        <div id="try-a-question" class="try-a-question__question-to-try">
            <Question
                :key="`${question.objectId}`"
                class="try-a-question__question"
                :ref="`questionContainer_${question.objectId}`"
                :container-el="questionContainerEl"
                :question="question"
                :question-number="1"
                :quiz-length="1"
                @checkAnswer="checkAnswer"
            />
        </div>
        <PocketButton
            v-if="checkedAnswer"
            class="try-a-question__see-stats-btn"
            @click="goToStats"
        >
            See Stats
        </PocketButton>
    </div>
</template>

<style lang='scss' scoped>
.try-a-question {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__banner {
        position: relative;
        display: flex;
        width: 260px;
        padding: 21px 12px 16px 12px;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        border-radius: 6px;
        border: 1px solid $butterscotch;
        background: $buttermilk;
        margin-top: 24px;
        margin-bottom: 40px;

        @include breakpoint(black-bear) {
            margin-bottom: 32px;
        }
    }

    &__banner-text {
        position: relative;
        z-index: 1;
        margin: 0;
        color: $brand-black;
        text-align: center;
        font-size: 15px;
        font-weight: 500;
        line-height: 22px;
    }

    &__half-tone {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: auto;
        z-index: 2;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        pointer-events: none;
    }

    &__star {
        position: absolute;
        top: 4px;
        left: -18px;
        width: 26px;
        height: 26px;
        z-index: 3;
        pointer-events: none;
        color: $caramel;
    }

    &__question-to-try {
        :deep(.uikit-question-context) {
            display: none;
        }

        :deep(.uikit-question__close-icon) {
            display: none;
        }
    }

    &__see-stats-btn {
        margin-bottom: 32px;
    }
}
</style>
